import { InventoryTree, SiteControlEvent } from '@scaleaq/sitecontrol-lib';
import { CellContext } from '@tanstack/react-table';
import { TFunction } from 'i18next';

import { ColumnType, SiteControlTableFeatures } from '@/components/table/';
import { findSensorInInventory } from '@/utils';

type GetColumns = (props: {
  inventory: InventoryTree[];
  inventoryLoading: boolean;
  t: TFunction<['common', 'eventStream']>;
  clipboardCallback: (value: string) => void;
}) => ColumnType<SiteControlEvent>[];

export const AccessorKeys = {
  sender: 'sender',
  sourcePath: 'sourcePath',
  timestamp: 'timestamp',
  type: 'type',
};

export const getColumns: GetColumns = ({ inventory, inventoryLoading, t, clipboardCallback }) => {
  const columns: ColumnType<SiteControlEvent>[] = [
    {
      type: 'accessor',
      accessorKey: AccessorKeys.timestamp,
      accessorFn: (n: SiteControlEvent) => new Date(n.timestamp).toLocaleString(),
      // header: t('eventStream:eventStream:headers.timestamp'),
      header: t('eventStream:headers.timestamp'),
      meta: { filterVariant: 'date' },
    },
    {
      type: 'accessor',
      accessorKey: AccessorKeys.type,
      header: t('eventStream:headers.type'),
      // note: originally cell content was formatted, but this complicates things for the user when
      // they want to filter and cannot just copy the value. Thus keep the type as is.
      // accessorFn: (cell: SiteControlEvent) => {
      //   const parts = cell.type.split('.');
      //   return parts.slice(-2).join(' ');
      // },
      cell: info => sourcePathCellRenderer(info, clipboardCallback, t),
    },
    {
      type: 'accessor',
      accessorKey: AccessorKeys.sender,
      header: t('eventStream:headers.sender'),
      cell: info => sourcePathCellRenderer(info, clipboardCallback, t),
    },
    {
      type: 'accessor',
      accessorKey: AccessorKeys.sourcePath,
      header: t('eventStream:headers.source'),
      accessorFn: (n: SiteControlEvent) => {
        const sensor = findSensorInInventory(inventory, n.inventoryId); //inventory?.find(s => n.source === s.id);
        return inventoryLoading
          ? t('loading')
          : sensor === null
            ? n.inventoryId // or return null and use cell render
            : (sensor.name ?? t('eventStream:notFound'));
      },
      cell: info => sourcePathCellRenderer(info, clipboardCallback, t),
    },
  ];

  return columns;
};

function sourcePathCellRenderer(
  info: CellContext<SiteControlTableFeatures, SiteControlEvent, unknown>,
  clipboardCallback: (value: string) => void,
  t: TFunction<['common', 'eventStream']>
) {
  const value: string = String(info.getValue());

  return (
    <span
      style={{ cursor: 'pointer' }}
      onClick={() => copyToClipboard(value).then(() => clipboardCallback(value))}
      title={t('eventStream:clickToCopy')}
    >
      {value}
    </span>
  );
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
  }
}
